import { Suggestion } from '@triumph/design-system';
import { compact, map } from 'lodash';
import { useEffect, useState } from 'react';

import { useDebtorAccount } from '@/app/shared/utils/hooks/useDebtorAccount';
import { postDebtorSearch } from '@/app/shared/api/endpoints/debtorSearch';
import {
  DebtorPermission,
  DebtorSearchResult,
} from '@/app/shared/contexts/DebtorAccountContext.types';
import { mapDebtorToSuggestion } from '@/app/shared/utils/mapDebtorToSuggestion';
import { DebtorSwitcher } from '.';
import { DebtorSwitcherWrapper } from './DebtorSwitcher.StyledComponents';

export const DebtorSwitcherContainer: React.FC = () => {
  const {
    isInternal,
    selectedDebtor,
    selectedDebtorName,
    selectDebtor,
    initialized,
  } = useDebtorAccount();

  const [search, setSearch] = useState('');
  const [rawSuggestions, setRawSuggestions] = useState<DebtorSearchResult[]>(
    [],
  );
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [debtorLoaded, setDebtorLoaded] = useState(false);

  useEffect(() => {
    if (!debtorLoaded && selectedDebtor?.debtorId) {
      setDebtorLoaded(true);
    }
  }, [selectedDebtor, debtorLoaded]);

  useEffect(() => {
    const trimmedSearch = compact(search.toUpperCase().split(' ')).join(' ');

    if (trimmedSearch.length <= 2 || !isInternal) {
      setSuggestions([]);
      setRawSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await postDebtorSearch(trimmedSearch, 10);

        setRawSuggestions(results || []);

        if (results != null && results.length > 0) {
          const mappedSuggestions = map(results, mapDebtorToSuggestion);
          setSuggestions(mappedSuggestions);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        setSuggestions([]);
        setRawSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [search, isInternal]);

  const handleChange = (searchValue: string) => {
    setSearch(searchValue);
  };

  const handleDebtorSelection = async (result: Suggestion) => {
    const debtorResult = rawSuggestions.find(
      (d) => d.id === result.id,
    );

    if (debtorResult) {
      await selectDebtor(
        {
          debtorId: Number(debtorResult.id),
          permissions: [DebtorPermission.DebtorPortalAccess],
        },
        debtorResult.name || '',
      );
    }
  };

  if (!initialized || !isInternal) {
    return null;
  }

  return (
    <DebtorSwitcherWrapper>
      <DebtorSwitcher
        id="debtor-switcher"
        suggestions={suggestions}
        onChange={handleChange}
        initialValue={selectedDebtorName || ''}
        onSelected={handleDebtorSelection}
        isLoading={isSearching}
      />
    </DebtorSwitcherWrapper>
  );
};